/* eslint-disable max-lines */
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useRecentSpeakerNames } from '@utils/hooks/use-recent-speaker-names.ts';

type SpeakerRenameFieldProps = {
  value: string;
  onChange: (value: string) => void;
  /** Pick a suggestion: fill the field and commit the rename. */
  onSelect: (name: string) => void;
  /** Commit the currently typed value (Enter / blur / Save). */
  onSubmit: () => void;
  /** Abandon the edit (Escape). */
  onCancel: () => void;
  /** Names from the speaker list / directory to suggest at the top. */
  listSuggestions: string[];
  name: string;
  placeholder?: string;
};

const MAX_PER_SECTION = 6;

/**
 * Inline speaker-rename input with a list-first suggestion dropdown.
 *
 * Shows speaker-list / directory matches on top, then — under a divider — a
 * "Recently used" section backed by localStorage (our stand-in for the browser
 * cache we suppress with autoComplete="off"). Each recent can be forgotten with
 * the ✕ button. This is the best-of-both UX: authoritative list suggestions
 * plus quick device-local recents, without the stale saved-form-data popover.
 *
 * The dropdown is rendered in a portal and positioned against the input's rect
 * because the field lives inside the picker's `overflow:auto` scroll container,
 * which would otherwise clip an absolutely-positioned menu.
 */
export const SpeakerRenameField = forwardRef<
  HTMLInputElement,
  SpeakerRenameFieldProps
>(
  (
    {
      value,
      onChange,
      onSelect,
      onSubmit,
      onCancel,
      listSuggestions,
      name,
      placeholder
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { getColour } = useThemeContext();
    const { recents, forget } = useRecentSpeakerNames();

    const innerRef = useRef<HTMLInputElement | null>(null);
    const setRefs = useCallback(
      (el: HTMLInputElement | null) => {
        innerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      },
      [ref]
    );

    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [rect, setRect] = useState<DOMRect | null>(null);
    // Track whether the user has actually typed into the field since it
    // mounted. Auto-focus + auto-select means the field starts populated with
    // the current speaker name — if we filter suggestions by that value the
    // dropdown collapses to zero matches and Martijn's browse-the-directory
    // ask fails (#2035). Once the user types, we honor the query normally.
    const [hasEdited, setHasEdited] = useState(false);
    // Reset when the field is remounted for a new rename session.
    useEffect(() => {
      setHasEdited(false);
    }, []);

    const query = hasEdited ? value.trim().toLowerCase() : '';

    const listMatches = useMemo(() => {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const raw of listSuggestions) {
        const trimmed = raw?.trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        if (seen.has(key)) continue;
        if (query && !key.includes(query)) continue;
        seen.add(key);
        out.push(trimmed);
        if (out.length >= MAX_PER_SECTION) break;
      }
      return out;
    }, [listSuggestions, query]);

    const recentMatches = useMemo(() => {
      const listKeys = new Set(listMatches.map(n => n.toLowerCase()));
      const out: string[] = [];
      for (const raw of recents) {
        const key = raw.toLowerCase();
        if (listKeys.has(key)) continue;
        if (query && !key.includes(query)) continue;
        out.push(raw);
        if (out.length >= MAX_PER_SECTION) break;
      }
      return out;
    }, [recents, listMatches, query]);

    const flat = useMemo(
      () => [...listMatches, ...recentMatches],
      [listMatches, recentMatches]
    );

    const open = focused && flat.length > 0;

    const updateRect = useCallback(() => {
      if (innerRef.current) setRect(innerRef.current.getBoundingClientRect());
    }, []);

    useLayoutEffect(() => {
      if (!open) return;
      updateRect();
      const handler = () => updateRect();
      window.addEventListener('scroll', handler, true);
      window.addEventListener('resize', handler);
      return () => {
        window.removeEventListener('scroll', handler, true);
        window.removeEventListener('resize', handler);
      };
    }, [open, flat.length, updateRect]);

    const move = (delta: number) => {
      if (!flat.length) return;
      setActiveIndex(prev => {
        const nextIndex = prev + delta;
        if (nextIndex < 0) return flat.length - 1;
        if (nextIndex >= flat.length) return 0;
        return nextIndex;
      });
    };

    const dropdown =
      open && rect
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('border'),
                color: getColour('normalText')
              }}
              className="z-[1000] max-h-64 overflow-y-auto rounded-xl border shadow-lg p-1"
              // Keep focus in the input so onBlur doesn't submit before a click.
              onMouseDown={e => e.preventDefault()}
            >
              {listMatches.length > 0 && (
                <>
                  <div
                    className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('in-speaker-list') || 'In speaker list'}
                  </div>
                  {listMatches.map((suggestion, i) => (
                    <button
                      key={`list-${suggestion}`}
                      type="button"
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => onSelect(suggestion)}
                      className="flex w-full items-center rounded-lg px-2 py-1.5 text-left text-sm"
                      style={{
                        backgroundColor:
                          activeIndex === i
                            ? getColour('secondLayer')
                            : undefined
                      }}
                    >
                      <span className="break-words">{suggestion}</span>
                    </button>
                  ))}
                </>
              )}

              {listMatches.length > 0 && recentMatches.length > 0 && (
                <div
                  className="my-1 h-px"
                  style={{ backgroundColor: getColour('hairline') }}
                />
              )}

              {recentMatches.length > 0 && (
                <>
                  <div
                    className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('recently-used') || 'Recently used'}
                  </div>
                  {recentMatches.map((suggestion, i) => {
                    const flatIndex = listMatches.length + i;
                    return (
                      <div
                        key={`recent-${suggestion}`}
                        onMouseEnter={() => setActiveIndex(flatIndex)}
                        className="flex w-full items-center rounded-lg pr-1"
                        style={{
                          backgroundColor:
                            activeIndex === flatIndex
                              ? getColour('secondLayer')
                              : undefined
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => onSelect(suggestion)}
                          className="flex flex-1 items-center px-2 py-1.5 text-left text-sm"
                        >
                          <span className="break-words">{suggestion}</span>
                        </button>
                        <button
                          type="button"
                          aria-label={t('remove-from-recents') || 'Remove'}
                          title={t('remove-from-recents') || 'Remove'}
                          onClick={() => forget(suggestion)}
                          className="shrink-0 rounded-md p-1 opacity-60 hover:opacity-100"
                          style={{ color: getColour('inactiveText') }}
                        >
                          <X width={13} />
                        </button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>,
            document.body
          )
        : null;

    return (
      <div className="relative flex-1">
        <input
          ref={setRefs}
          autoFocus
          type="text"
          name={name}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={e => {
            onChange(e.target.value);
            setActiveIndex(-1);
            // First keystroke → start filtering suggestions by the typed
            // value. Before this, dropdown shows the full browsable list.
            if (!hasEdited) setHasEdited(true);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onSubmit();
          }}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              move(1);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              move(-1);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              if (open) {
                setActiveIndex(-1);
                setFocused(false);
              } else {
                onCancel();
              }
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (open && activeIndex >= 0 && flat[activeIndex]) {
                onSelect(flat[activeIndex]);
              } else {
                onSubmit();
              }
            }
          }}
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border'),
            color: getColour('normalText')
          }}
          className="w-full rounded-2xl border px-2 py-1 text-sm outline-none"
          placeholder={placeholder}
        />
        {dropdown}
      </div>
    );
  }
);

SpeakerRenameField.displayName = 'SpeakerRenameField';
